"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.removeQueryFromPath = exports.extractDomain = exports.getCurrentDomainUrl = exports.buildCurrentDomainUrl = exports.buildUrl = exports.loadForAtLeast = exports.retry = void 0;
/**
 * Retry a certain amount of times (default: 3 times) if the provided action threw an error.
 * @param action async function to call
 * @param options.retryCount number of times to retry (default 3)
 * @param options.retryDelay delay before retrying
 */
async function retry(action, options = {}) {
    const opts = {
        retryCount: 3,
        retryDelay: 1000,
        ...options,
    };
    try {
        return await action(opts.retryCount);
    }
    catch (error) {
        if (opts.retryCount > 0 && error) {
            opts.retryCount--;
            await new Promise(resolve => setTimeout(resolve, opts.retryDelay));
            return await retry(action, opts);
        }
        throw error;
    }
}
exports.retry = retry;
/**
 * The spinner should be displayed for at least `minRequestTime`. Prevent flashing the
 * spinner when the request is almost instant.
 * Handles both success and error cases to ensure minimum loading time is respected.
 */
function loadForAtLeast(promise, minRequestTime = 400) {
    const timeBeforeRequest = new Date().getTime();
    /**
     * Calculates remaining time and returns a promise that waits if needed.
     * @param value - The value to resolve/reject with
     * @param shouldReject - Whether to reject (true) or resolve (false)
     */
    const waitIfNeeded = (value, shouldReject) => {
        const timeAfterRequest = new Date().getTime();
        const requestExecutionTime = timeAfterRequest - timeBeforeRequest;
        const loadingStateRemainingTime = minRequestTime - requestExecutionTime;
        if (loadingStateRemainingTime > 0) {
            return new Promise((resolve, reject) => {
                setTimeout(() => {
                    shouldReject ? reject(value) : resolve(value);
                }, loadingStateRemainingTime);
            });
        }
        if (shouldReject)
            throw value;
        return Promise.resolve(value);
    };
    return promise.then(data => waitIfNeeded(data, false)).catch(error => waitIfNeeded(error, true));
}
exports.loadForAtLeast = loadForAtLeast;
function buildUrl(domain, ...paths) {
    return `${domain.replace(/\/$/, '')}/${paths.map(ensureLeadingSlash).join('')}`;
}
exports.buildUrl = buildUrl;
function ensureLeadingSlash(rawPath) {
    if (!rawPath)
        return rawPath;
    const [routePath, routeQuery = ''] = rawPath.split('?');
    const parts = [routePath.replace(/^\//, '').replace(/\/?$/, '/')];
    if (routeQuery)
        parts.push(routeQuery);
    return parts.join('?');
}
function buildCurrentDomainUrl(reqOrPath, ...otherPaths) {
    const req = typeof reqOrPath === 'string' ? undefined : reqOrPath;
    const paths = typeof reqOrPath === 'string' ? [reqOrPath, ...otherPaths] : otherPaths;
    const currentDomain = getCurrentDomainUrl(req);
    if (currentDomain)
        return buildUrl(currentDomain, ...paths);
}
exports.buildCurrentDomainUrl = buildCurrentDomainUrl;
function getCurrentDomainUrl(req) {
    return req
        ? getCurrentServerDomainUrl(req)
        : typeof window !== 'undefined'
            ? getCurrentBrowserDomainUrl()
            : undefined;
}
exports.getCurrentDomainUrl = getCurrentDomainUrl;
function getCurrentBrowserDomainUrl() {
    const { protocol, host } = window.location;
    return `${protocol}//${host}`;
}
function getCurrentServerDomainUrl(req) {
    const protocol = process.env.NODE_ENV === 'development' ? 'http' : 'https';
    const forwarded = req.headers?.['x-forwarded-host'];
    const host = (typeof forwarded === 'string' ? forwarded : forwarded?.[0]) ?? req.headers?.host;
    return host ? `${protocol}://${host}` : undefined;
}
/**
 * Extract the domain from a hostname (e.g. "www.acces-impot.com" -> "acces-impot.com")
 * @param hostname The hostname to extract the domain from
 * @returns The domain
 */
function extractDomain(hostname) {
    return hostname.split('.').slice(-2).join('.');
}
exports.extractDomain = extractDomain;
/**
 * Remove specific query parameter from path
 */
function removeQueryFromPath(path, query) {
    return path.replace(new RegExp(`([?&])${query}(=[^&#]+)?(&?)`), '$1').replace(/(\?|&)($|#)/, '$2');
}
exports.removeQueryFromPath = removeQueryFromPath;
//# sourceMappingURL=index.js.map